import { computed, reactive } from 'vue'

import spaApiClient from '@/Api/spaApiClient.ts'
// eslint-disable-next-line import/extensions
import { trans } from '@/Translate'
import route from '@/Util/route.js'

export default function useCheckout(storeDomain, cart, isApi, isPreview) {
  spaApiClient.setup(isApi, storeDomain, cart)

  const state = reactive({
    addDiscountWorking: false,
    removeDiscountWorking: false,
    calculateTaxWorking: false,
    updateCartItemWorking: false,
  })

  const working = computed(() => {
    return (
      state.addDiscountWorking ||
      state.removeDiscountWorking ||
      state.calculateTaxWorking ||
      state.validateWorking ||
      state.updateCartItemWorking
    )
  })

  function $route(name, params = {}) {
    if (isApi) {
      return route(
        `spa-api.${name}`,
        {
          cart: cart.id,
          ...params,
        },
        undefined,
      )
    }

    return route(
      name,
      {
        domain: storeDomain,
        cart: cart.id,
        ...params,
      },
      undefined,
    )
  }

  function setLocale(locale) {
    if (!locale) {
      return Promise.reject(trans('lemonsqueezy_checkout.form.locale_required'))
    }

    return new Promise((resolve, reject) => {
      spaApiClient
        .setLocale(locale)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  function saveEmail(email) {
    if (!email) {
      return Promise.reject(trans('lemonsqueezy_checkout.form.email_required'))
    }

    return new Promise((resolve, reject) => {
      spaApiClient
        .saveEmail(email)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  function updateCartItem(payload) {
    return new Promise((resolve, reject) => {
      state.updateCartItemWorking = true

      spaApiClient
        .updateCartItem(payload)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => (state.updateCartItemWorking = false))
    })
  }

  function validate(payload, service) {
    return new Promise((resolve, reject) => {
      spaApiClient
        .validate(payload, service)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  function confirmEmail(email) {
    return new Promise((resolve, reject) => {
      state.confirmEmailWorking = true

      spaApiClient
        .confirmEmail(email)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => (state.confirmEmailWorking = false))
    })
  }

  function addDiscount(discountCode, service) {
    return new Promise((resolve, reject) => {
      if (isPreview) {
        return reject(trans('lemonsqueezy_checkout.form.discount_not_available_in_preview'))
      }

      if (!discountCode) {
        return reject(trans('lemonsqueezy_checkout.form.discount_code_required'))
      }

      state.addDiscountWorking = true

      spaApiClient
        .addDiscount(discountCode, service)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => (state.addDiscountWorking = false))
    })
  }

  function removeDiscount() {
    return new Promise((resolve, reject) => {
      if (isPreview) {
        return reject(trans('lemonsqueezy_checkout.form.remove_discount_not_available_in_preview'))
      }

      state.removeDiscountWorking = true

      spaApiClient
        .removeDiscount()
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => (state.removeDiscountWorking = false))
    })
  }

  function calculateTax(formData) {
    return new Promise((resolve, reject) => {
      if (isPreview) {
        return reject(trans('lemonsqueezy_checkout.form.calculate_tax_not_available_in_preview'))
      }

      if (!formData.country || !formData.postal_code) {
        return reject(trans('lemonsqueezy_checkout.form.country_and_postal_code_required'))
      }

      if (
        formData.country === 'US' &&
        (!formData.line1 || !formData.city || !formData.state || !formData.postal_code)
      ) {
        return reject(trans('lemonsqueezy_checkout.form.address_line_1_city_and_state_required'))
      }

      state.calculateTaxWorking = true

      const bodyParams = {
        country: formData.country,
        postal_code: formData.postal_code,
        tax_number: formData.tax_number,
      }

      if (formData.country === 'US') {
        bodyParams.line1 = formData.line1
        bodyParams.line2 = formData.line2
        bodyParams.city = formData.city
        bodyParams.state = formData.state
      }

      spaApiClient
        .calculateTax(bodyParams)
        .then((data) => resolve(data))
        .catch((error) => reject(error))
        .finally(() => (state.calculateTaxWorking = false))
    })
  }

  function requestTaxExemptLink() {
    return new Promise((resolve, reject) => {
      spaApiClient
        .requestTaxExemptLink()
        .then((data) => resolve(data))
        .catch((error) => reject(error))
    })
  }

  return {
    working,
    $route,
    saveEmail,
    addDiscount,
    removeDiscount,
    calculateTax,
    updateCartItem,
    setLocale,
    validate,
    requestTaxExemptLink,
    confirmEmail,
  }
}
